import MuiModal from "@mui/material/Modal";
import Slide from "@mui/material/Slide";
import { RedoButton } from "@redotech/arbiter/buttons/redo-button";
import { RedoCommandShortcut } from "@redotech/arbiter/command-menu/redo-command-shortcut";
import { Flex } from "@redotech/arbiter/flex";
import { RedoTextInput } from "@redotech/arbiter/input/redo-text-input";
import { Text } from "@redotech/arbiter/text";
import SearchIcon from "@redotech/redo-icons/search-md.svg";
import XCloseIcon from "@redotech/redo-icons/x-close.svg";
import { getShortcutDisplayParts } from "@redotech/redo-web/keyboard-shortcuts/keyboard-shortcut";
import classNames from "classnames";
import {
  Fragment,
  memo,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
  type PointerEvent as ReactPointerEvent,
} from "react";
import * as styles from "./keyboard-shortcuts-modal.module.css";
import {
  SHORTCUT_CATEGORIES,
  ShortcutCategory,
  ShortcutEntry,
} from "./shortcut-catalog";

const DISMISS_DRAG_PX = 96;
const SHEET_TOP_GAP_PX = 16;

function sheetHostStyle(
  lift: number,
): CSSProperties & { "--sheet-lift": string } {
  return { "--sheet-lift": `${lift}px` };
}

/** Above the phone bar and floating popups; menus and tooltips stay above. */
const SHEET_Z_INDEX = 2_147_483_005;

// Words a merchant might type to find a modifier, so "cmd", "ctrl", "option",
// etc. all match the corresponding shortcuts.
const MODIFIER_SEARCH_WORDS: Record<string, string[]> = {
  meta: ["cmd", "command", "ctrl", "control"],
  ctrl: ["ctrl", "control"],
  alt: ["alt", "option", "opt"],
  shift: ["shift"],
};

/** Lowercased text a row is matched against: action, category, and its keys. */
function entryHaystack(entry: ShortcutEntry, categoryLabel: string): string {
  const keyBits = entry.shortcuts.flatMap((shortcut) => [
    shortcut.keyLabel,
    ...(shortcut.modifiers ?? []).flatMap(
      (modifier) => MODIFIER_SEARCH_WORDS[modifier] ?? [],
    ),
  ]);
  return [entry.description, categoryLabel, ...keyBits].join(" ").toLowerCase();
}

export const KeyboardShortcutsModal = memo(function KeyboardShortcutsModal({
  isOpen,
  onClose,
}: {
  isOpen: boolean;
  onClose: () => void;
}) {
  const [search, setSearch] = useState("");
  const [dragY, setDragY] = useState(0);
  const [lift, setLift] = useState(0);
  const [dragging, setDragging] = useState(false);
  const hostRef = useRef<HTMLDivElement>(null);
  const dragSession = useRef<{
    pointerId: number;
    startY: number;
    originLift: number;
    maxLift: number;
  } | null>(null);
  const wasOpen = useRef(isOpen);

  if (wasOpen.current !== isOpen) {
    wasOpen.current = isOpen;
    if (isOpen && (dragY !== 0 || lift !== 0 || dragging)) {
      setDragY(0);
      setLift(0);
      setDragging(false);
    }
  }

  const query = search.trim().toLowerCase();

  // Filter every category's rows by the text query (matches action, key, or
  // category name); an empty query shows everything.
  const visibleCategories = useMemo(() => {
    if (!query) {
      return SHORTCUT_CATEGORIES;
    }
    return SHORTCUT_CATEGORIES.map((category) => ({
      ...category,
      entries: category.entries.filter((entry) =>
        entryHaystack(entry, category.label).includes(query),
      ),
    })).filter((category) => category.entries.length > 0);
  }, [query]);

  function onHandlePointerDown(event: ReactPointerEvent<HTMLDivElement>) {
    try {
      event.currentTarget.setPointerCapture(event.pointerId);
    } catch {
      // The pointer is already gone. The drag still follows pointer events.
    }
    const host = hostRef.current;
    const pane = host?.offsetParent;
    const room =
      host && pane instanceof HTMLElement
        ? host.getBoundingClientRect().top -
          pane.getBoundingClientRect().top -
          SHEET_TOP_GAP_PX
        : 0;
    dragSession.current = {
      pointerId: event.pointerId,
      startY: event.clientY,
      originLift: lift,
      maxLift: Math.max(0, lift + room),
    };
    setDragging(true);
  }

  function onHandlePointerMove(event: ReactPointerEvent<HTMLDivElement>) {
    const session = dragSession.current;
    if (!session || session.pointerId !== event.pointerId) {
      return;
    }
    const raw = session.originLift + (session.startY - event.clientY);
    setLift(Math.min(session.maxLift, Math.max(0, raw)));
    setDragY(raw < 0 ? -raw : 0);
  }

  function onHandlePointerUp(event: ReactPointerEvent<HTMLDivElement>) {
    const session = dragSession.current;
    if (!session || session.pointerId !== event.pointerId) {
      return;
    }
    dragSession.current = null;
    setDragging(false);
    const raw = session.originLift + (session.startY - event.clientY);
    const dragDown = raw < 0 ? -raw : 0;
    if (dragDown > DISMISS_DRAG_PX) {
      // <Slide>'s exit runs the same animation as Escape and the close
      // button. dragY is left as-is so the sheet carries on from where the
      // pointer released instead of snapping back first.
      onClose();
      return;
    }
    setLift(Math.min(session.maxLift, Math.max(0, raw)));
    setDragY(0);
  }

  return (
    <MuiModal
      BackdropProps={{ className: styles.backdrop, invisible: true }}
      className={styles.modalRoot}
      closeAfterTransition
      disableAutoFocus
      onClose={onClose}
      open={isOpen}
      style={{ zIndex: SHEET_Z_INDEX }}
    >
      <Slide
        appear
        direction="up"
        easing={{
          enter: "cubic-bezier(0.16, 1, 0.3, 1)",
          exit: "cubic-bezier(0.4, 0, 1, 1)",
        }}
        in={isOpen}
        mountOnEnter
        timeout={{ enter: 380, exit: 240 }}
        unmountOnExit
      >
        <div
          aria-label="Keyboard shortcuts"
          aria-modal="true"
          className={classNames(styles.sheetHost, dragging && styles.dragging)}
          ref={hostRef}
          role="dialog"
          style={sheetHostStyle(lift)}
          tabIndex={-1}
        >
          <Flex
            bgColor="primary"
            borderColor="secondary"
            borderStyle="solid"
            borderTopLeftRadius="3xl"
            borderTopRightRadius="3xl"
            borderWidth="1px"
            className={classNames(styles.sheet, dragging && styles.dragging)}
            dir="column"
            height="full"
            overflow="hidden"
            style={{ transform: `translateY(${dragY}px)` }}
          >
            <Flex
              align="center"
              className={styles.handleHit}
              justify="center"
              onPointerCancel={onHandlePointerUp}
              onPointerDown={onHandlePointerDown}
              onPointerMove={onHandlePointerMove}
              onPointerUp={onHandlePointerUp}
              pb="sm"
              pt="md"
              shrink={0}
              w="full"
            >
              <div className={styles.handle} />
            </Flex>
            <Flex
              align="flex-start"
              gap="lg"
              justify="space-between"
              px="3xl"
              shrink={0}
            >
              <Flex dir="column" gap="xs">
                <Text fontSize="lg" fontWeight="semibold" textColor="primary">
                  Keyboard shortcuts
                </Text>
                <Text fontSize="sm" textColor="secondary">
                  These keys work when you are not typing a reply. ⌘ and ⌥
                  shortcuts use Ctrl and Alt on Windows and Linux, respectively.
                </Text>
              </Flex>
              <RedoButton
                aria-label="Close keyboard shortcuts"
                hierarchy="tertiary"
                IconLeading={XCloseIcon}
                onClick={onClose}
                size="sm"
              />
            </Flex>
            <Flex
              dir="column"
              gap="3xl"
              grow={1}
              minH="none"
              overflow="hidden"
              pt="xl"
              px="3xl"
            >
              <RedoTextInput
                autoFocus
                fullWidth
                IconLeading={SearchIcon}
                placeholder="Search by action, key, or category"
                setValue={setSearch}
                value={search}
              />
              <Flex
                className={styles.listEnd}
                dir="column"
                grow={1}
                minH="none"
                overflowY="auto"
              >
                {visibleCategories.length === 0 ? (
                  <Text fontSize="sm" textColor="secondary">
                    No shortcuts match “{search}”
                  </Text>
                ) : (
                  <div className={styles.shortcutColumns}>
                    {visibleCategories.map((category) => (
                      <ShortcutSection category={category} key={category.key} />
                    ))}
                  </div>
                )}
              </Flex>
            </Flex>
          </Flex>
        </div>
      </Slide>
    </MuiModal>
  );
});

const ShortcutSection = memo(function ShortcutSection({
  category,
}: {
  category: ShortcutCategory;
}) {
  return (
    <Flex className={styles.shortcutSection} dir="column" gap="sm" mb="4xl">
      <Text
        className={styles.sectionHeader}
        fontSize="xxs"
        fontWeight="semibold"
        textColor="tertiary"
      >
        {category.label}
      </Text>
      <Flex dir="column" gap="none">
        {category.entries.map((entry) => (
          <ShortcutRow entry={entry} key={entry.description} />
        ))}
      </Flex>
    </Flex>
  );
});

const ShortcutRow = memo(function ShortcutRow({
  entry,
}: {
  entry: ShortcutEntry;
}) {
  // Collapse combos that display identically (e.g. the same keys bound in more
  // than one scope) so a row never shows the same chip twice.
  const seenCombos = new Set<string>();
  const keyGroups = entry.shortcuts
    .map((shortcut) => getShortcutDisplayParts(shortcut))
    .filter((parts) => {
      const combo = parts.join("+");
      if (seenCombos.has(combo)) {
        return false;
      }
      seenCombos.add(combo);
      return true;
    });

  return (
    <Flex align="center" gap="md" justify="space-between" py="sm">
      <Text fontSize="sm" textColor="primary">
        {entry.description}
      </Text>
      <Flex align="center" gap="sm" shrink={0}>
        {keyGroups.map((parts, index) => (
          <Fragment key={index}>
            {index > 0 && (
              <Text fontSize="xs" textColor="secondary">
                or
              </Text>
            )}
            <Flex align="center" gap="xs">
              {parts.map((part, partIndex) => (
                <RedoCommandShortcut
                  background
                  hotkey={[part]}
                  key={partIndex}
                  size="md"
                />
              ))}
            </Flex>
          </Fragment>
        ))}
      </Flex>
    </Flex>
  );
});
